import React, { useState, useRef, useCallback } from 'react';
import type { PageDetailOut } from '../types/catalog';
import { Button } from '@/components/ui/Button';
import { DrawingCanvas, DrawingCanvasRef } from '../components/DrawingCanvas';
import { useAuth } from '@/contexts/AuthContext';
import { showSuccessToastCenter } from '@/utils/errorHandler';

interface TracingTarget {
  id: string;
  text?: string;
  guide?: {
    image?: { url?: string; alt?: string };
    stroke_points?: unknown[];
  };
}

/** Section with type "tracing_multi" has targets and options at top level */
interface TracingMultiSection {
  type: 'tracing_multi';
  targets?: TracingTarget[];
  default_target_id?: string;
  ui_hints?: {
    colors?: string[];
    brush_sizes?: number[];
    show_clear_button?: boolean;
    show_submit_button?: boolean;
  };
  show_clear_button?: boolean;
  show_submit_button?: boolean;
  [key: string]: unknown;
}

interface Section {
  type?: string;
  text?: string;
  heading?: string;
  paragraph?: string;
  targets?: TracingTarget[];
  default_target_id?: string;
  ui_hints?: TracingMultiSection['ui_hints'];
  [key: string]: unknown;
}

function getSections(page: PageDetailOut): Section[] {
  const content = page.page_json_v1?.content as { sections?: Section[] } | undefined;
  return content?.sections ?? [];
}

function getText(section: Section): string {
  return (section.text ?? section.heading ?? section.paragraph ?? '') as string;
}

const DEFAULT_COLORS = ['#000000', '#2563eb', '#dc2626', '#16a34a'];
const DEFAULT_BRUSH_SIZES = [4, 8, 16];

const STORAGE_PREFIX = 'tkm_tracing';

function getStorageKey(studentId: string, pageId: string, targetId: string): string {
  return `${STORAGE_PREFIX}:${studentId}:${pageId}:${targetId}`;
}

export function saveTracingResult(
  studentId: string,
  pageId: string,
  targetId: string,
  pngBase64: string
): void {
  try {
    const key = getStorageKey(studentId, pageId, targetId);
    const value = JSON.stringify({
      png_base64: pngBase64,
      created_at: new Date().toISOString(),
    });
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

interface PracticeTracingV1Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

export const PracticeTracingV1: React.FC<PracticeTracingV1Props> = ({ page, onComplete }) => {
  const { user } = useAuth();
  const studentId = user?.id ?? 'demo-student';
  const sections = getSections(page);
  const heading = sections.find((s) => (s.type ?? '').toLowerCase() === 'heading');
  const paragraph = sections.find((s) => (s.type ?? '').toLowerCase() === 'paragraph');
  const tracingSection = sections.find((s) => s.type === 'tracing_multi') as TracingMultiSection | undefined;
  const targets = tracingSection?.targets ?? [];
  const defaultId = tracingSection?.default_target_id ?? targets[0]?.id ?? '';
  const uiHints = tracingSection?.ui_hints ?? {};
  const colors = uiHints.colors?.length ? uiHints.colors : DEFAULT_COLORS;
  const brushSizes = uiHints.brush_sizes?.length ? uiHints.brush_sizes : DEFAULT_BRUSH_SIZES;
  const showClear = tracingSection?.ui_hints?.show_clear_button !== false;
  const showSubmit = tracingSection?.ui_hints?.show_submit_button !== false;
  const showTargetPicker = true;

  const [selectedTargetId, setSelectedTargetId] = useState<string>(defaultId ?? '');
  const [color, setColor] = useState<string>(colors[0] ?? '#000000');
  const [brushSize, setBrushSize] = useState<number>(brushSizes[0] ?? 8);
  const canvasRef = useRef<DrawingCanvasRef>(null);

  // Clear canvas when switching targets so previous strokes don't carry over
  React.useEffect(() => {
    canvasRef.current?.clear();
  }, [selectedTargetId]);

  const selectedTarget = targets.find((t: TracingTarget) => t.id === selectedTargetId);
  const guideImageUrl = selectedTarget?.guide?.image?.url?.trim() || null;
  const guideText = selectedTarget?.text ?? selectedTarget?.id ?? '';

  const handleClear = useCallback(() => {
    canvasRef.current?.clear();
  }, []);

  const handleSubmit = useCallback(() => {
    const base64 = canvasRef.current?.getPngBase64();
    if (!base64 || !selectedTargetId || !page.id) return;
    saveTracingResult(studentId, page.id, selectedTargetId, base64);
    showSuccessToastCenter('Saved ✅');
    onComplete?.();
  }, [studentId, page.id, selectedTargetId, onComplete]);

  const canvasWidth = 600;
  const canvasHeight = 400;

  if (targets.length === 0) {
    return (
      <div className="p-6 text-gray-500 dark:text-gray-400">
        No tracing targets in this page.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6 overflow-auto">
      {heading && (
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          {getText(heading)}
        </h1>
      )}
      {paragraph && (
        <p className="text-gray-700 dark:text-gray-300">{getText(paragraph)}</p>
      )}

      <div className="max-w-4xl mx-auto w-full px-4">
        <div className="flex justify-center">
          <div className="w-full max-w-[900px] relative">
            {/* Target picker */}
            <div className="flex flex-wrap gap-2 mb-4 justify-center">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 self-center mr-2">
                Target:
              </span>
              {targets.map((t: TracingTarget) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTargetId(t.id)}
                  className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                    selectedTargetId === t.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {t.text ?? t.id}
                </button>
              ))}
            </div>

            {/* Controls (colors + kid-friendly brush sizes) */}
            <div className="flex justify-center mb-4">
              <div className="flex flex-col gap-3 items-center">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mr-2">Color:</span>
                  {colors.map((c: string) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`w-8 h-8 rounded-full border-2 ${color === c ? 'border-gray-900 dark:border-white scale-110' : 'border-gray-300 dark:border-gray-600'}`}
                      style={{ backgroundColor: c }}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mr-2">Brush:</span>
                  {(() => {
                    const defaultBrushValues = [6, 10, 14];
                    const sizes = brushSizes && brushSizes.length ? brushSizes : defaultBrushValues;
                    const labels = ['சிறியது', 'நடுத்தரம்', 'பெரியது'];
                    const english = ['Small', 'Medium', 'Large'];
                    const opts = sizes.slice(0, 3).map((val, i) => ({
                      label: labels[i] ?? english[i],
                      value: val,
                      dots: i + 1,
                    }));
                    return opts.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setBrushSize(opt.value)}
                        className={`px-3 py-1 rounded border text-sm flex items-center gap-2 ${brushSize === opt.value ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30' : 'border-gray-200 dark:border-gray-700'}`}
                      >
                        <span className="text-lg leading-none">{'●'.repeat(opt.dots)}</span>
                        <span className="text-sm">{opt.label}</span>
                      </button>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Canvas */}
            <div className="flex justify-center">
              <div className="w-full max-w-[900px]">
                <DrawingCanvas
                  ref={canvasRef}
                  width={canvasWidth}
                  height={canvasHeight}
                  color={color}
                  brushSize={brushSize}
                  backgroundImageUrl={guideImageUrl}
                  backgroundText={guideText}
                  className="w-full"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-2 mt-4">
              {showClear && (
                <Button variant="outline" size="sm" onClick={handleClear}>
                  Clear
                </Button>
              )}
              {showSubmit && (
                <Button variant="primary" size="sm" onClick={handleSubmit}>
                  Submit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
